import React from "react";
import MuiTypography from "@mui/material/Typography";
import { LinkifiedText } from "@/shared/ui";

export type TypographyWithLinkifyProps = React.ComponentProps<typeof MuiTypography> & {
  /** When false, disables automatic URL linkification. Default: true for body variants */
  linkify?: boolean;
};

/**
 * Wrapper for MUI Typography that automatically converts URLs in text to clickable links.
 * Used as the default Typography component via theme - applies to body1, body2, caption
 * when children is a string. Pass linkify={false} to disable.
 */
const TypographyWithLinkify: React.FC<TypographyWithLinkifyProps> = (props) => {
  const { children, variant, linkify, ...rest } = props;
  const componentProp = (rest as { component?: React.ElementType }).component;

  const bodyVariants = ["body1", "body2", "caption"];
  const isBodyVariant =
    variant && bodyVariants.includes(variant as string);
  const shouldLinkify =
    linkify !== false &&
    (linkify === true || (isBodyVariant && typeof children === "string"));

  const content =
    shouldLinkify && typeof children === "string" ? (
      <LinkifiedText>{children}</LinkifiedText>
    ) : (
      children
    );

  // Use DOM element to avoid reusing TypographyWithLinkify (would cause infinite loop)
  const safeComponent =
    componentProp === TypographyWithLinkify ? "p" : componentProp ?? "p";

  const passThrough = rest as Record<string, unknown>;
  const { component: _c, ...restWithoutComponent } = passThrough;
  return (
    <MuiTypography
      variant={variant ?? "body1"}
      component={safeComponent as "span"}
      {...(restWithoutComponent as any)}
    >
      {content}
    </MuiTypography>
  );
};

export default TypographyWithLinkify;
